import { useEffect, useState, useRef } from "react";
import { motion, Reorder, useDragControls } from "framer-motion";
import { Plus, Trash2, Pencil, Loader2, Newspaper, Mail, CalendarDays, Video, Upload, Eye, Star, Settings2, Image, Paperclip, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { uploadAnyFile } from "@/lib/uploads";
import AttachmentCard from "@/components/AttachmentCard";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import ArticleBody from "@/components/ArticleBody";
import RichTextEditor from "@/components/RichTextEditor";
import { BlockView, getVideoEmbed } from "@/components/SectionBlocks";
import { VideoThumb } from "@/components/VideoThumb";
import { ShareButtons } from "@/components/ShareButtons";
import { CoverReposition } from "@/components/CoverReposition";
import { Link } from "react-router-dom";

const empty = { title: "", body: "", image_url: "", images: [], image_position: "50% 50%", video_url: "", discipline: "", category: "notizia", published: true, featured: false, publish_at: "" };

// Immagine di copertina trascinabile per riordino (la prima è la copertina riposizionabile)
function NewsImageItem({ url, idx, position, onReposition, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={url} dragListener={false} dragControls={controls} data-testid={`news-image-item-${idx}`} className="rounded-lg border border-white/10 p-2 bg-black/20">
      <div className="flex items-start gap-2">
        <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`news-image-drag-${idx}`} title="Trascina per riordinare" className="shrink-0 p-1.5 text-muted-foreground hover:text-gold cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-4 h-4" /></button>
        {idx === 0 ? (
          <div className="flex-1 min-w-0">
            <span className="text-[11px] text-gold uppercase tracking-wider">Copertina</span>
            <div className="mt-1"><CoverReposition src={mediaUrl(url)} position={position} onChange={onReposition} /></div>
          </div>
        ) : (
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <img src={mediaUrl(url)} alt={`immagine ${idx + 1}`} className="w-24 h-24 object-cover rounded border border-white/10" />
            <span className="text-xs text-muted-foreground">Immagine {idx + 1}</span>
          </div>
        )}
        <button type="button" data-testid={`news-image-remove-${idx}`} onClick={onRemove} title="Rimuovi immagine" className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
      </div>
    </Reorder.Item>
  );
}


// Video inline nel corpo: normalizza un link a embed YouTube/Vimeo
const buildVideoLinkHtml = (raw) => {
  const url = (raw || "").trim();
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  let embed = null;
  if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
  else if (vimeo) embed = `https://player.vimeo.com/video/${vimeo[1]}`;
  if (!embed) return null;
  return `<iframe class="ql-video" src="${embed}" frameborder="0" allowfullscreen></iframe>`;
};
const buildVideoFileHtml = (src) => `<video controls controlslist="nodownload" playsinline class="ql-article-video" src="${src}"></video>`;
const videoEmbedUrl = (raw) => {
  const url = (raw || "").trim();
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};
const youtubeThumb = (embedSrc) => {
  const m = (embedSrc || "").match(/youtube\.com\/embed\/([\w-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
};
// Estrae in ordine i video presenti nel corpo (iframe YouTube/Vimeo + file)
const parseBodyVideos = (html) => {
  const doc = new DOMParser().parseFromString(`<div id="__root">${html || ""}</div>`, "text/html");
  const root = doc.getElementById("__root");
  const nodes = root ? Array.from(root.querySelectorAll("iframe, video")) : [];
  return nodes.map((n) => {
    const src = n.getAttribute("src") || "";
    if (n.tagName.toLowerCase() === "video") return { kind: "file", src, label: "Video caricato" };
    const label = src.includes("vimeo") ? "Vimeo" : "YouTube";
    return { kind: "embed", src, label };
  });
};
const removeBodyVideoAt = (html, index) => {
  const doc = new DOMParser().parseFromString(`<div id="__root">${html || ""}</div>`, "text/html");
  const root = doc.getElementById("__root");
  const nodes = root ? Array.from(root.querySelectorAll(".ql-video, .ql-article-video")) : [];
  if (nodes[index]) nodes[index].remove();
  return root ? root.innerHTML : html;
};
// Immagini inline nel corpo (escluse quelle dentro le gallerie)
const buildImageHtml = (src) => `<p><img src="${src}"></p>`;
const _bodyImageNodes = (root) => (root ? Array.from(root.querySelectorAll("img")).filter((n) => !n.closest(".article-gallery")) : []);
const parseBodyImages = (html) => {
  const doc = new DOMParser().parseFromString(`<div id="__root">${html || ""}</div>`, "text/html");
  return _bodyImageNodes(doc.getElementById("__root")).map((n) => ({ src: n.getAttribute("src") || "" }));
};
const removeBodyImageAt = (html, index) => {
  const doc = new DOMParser().parseFromString(`<div id="__root">${html || ""}</div>`, "text/html");
  const root = doc.getElementById("__root");
  const nodes = _bodyImageNodes(root);
  if (nodes[index]) { const p = nodes[index].closest("p"); (p && p.querySelectorAll("img").length === 1 ? p : nodes[index]).remove(); }
  return root ? root.innerHTML : html;
};

export default function NewsSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [news, setNews] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmFeature, setConfirmFeature] = useState(null);
  const [nlDialog, setNlDialog] = useState(null);
  const [nlSegment, setNlSegment] = useState("");
  const [nlEmail, setNlEmail] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [nlConfirm, setNlConfirm] = useState(false);

  const emailTplEmpty = { header_title: "", accent_color: "#D4AF37", footer_text: "", bg_color: "#f4f4f5", logo_url: "" };
  const emailPresets = [
    { key: "oro", label: "Oro classico", accent_color: "#D4AF37", bg_color: "#f4f4f5" },
    { key: "scuro", label: "Scuro", accent_color: "#FDE047", bg_color: "#0F172A" },
    { key: "chiaro", label: "Chiaro", accent_color: "#2563EB", bg_color: "#ffffff" },
  ];
  const [emailTplOpen, setEmailTplOpen] = useState(false);
  const [emailTpl, setEmailTpl] = useState(emailTplEmpty);
  const [emailTplSaving, setEmailTplSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const editorRef = useRef(null);
  const [articleVideos, setArticleVideos] = useState([]);
  const [articleImages, setArticleImages] = useState([]);
  const [articleFiles, setArticleFiles] = useState([]);
  const refreshMedia = () => {
    setArticleVideos(editorRef.current?.getVideos?.() || []);
    setArticleImages(editorRef.current?.getImages?.() || []);
    setArticleFiles(editorRef.current?.getAttachments?.() || []);
  };
  useEffect(() => {
    if (!open) return;
    const timers = [setTimeout(refreshMedia, 350), setTimeout(refreshMedia, 900)];
    return () => timers.forEach(clearTimeout);
  }, [open]);
  const [addVideoLink, setAddVideoLink] = useState("");
  const [addingArticleVideo, setAddingArticleVideo] = useState(false);
  const addArticleVideoLink = () => {
    const embed = videoEmbedUrl(addVideoLink);
    if (!embed) { toast.error("Link non valido. Usa YouTube o Vimeo."); return; }
    editorRef.current?.insertVideoEmbed(embed);
    setAddVideoLink("");
    setTimeout(refreshMedia, 120);
    toast.success("Video aggiunto in fondo all'articolo");
  };
  const uploadArticleVideoFile = async (file) => {
    if (!file) return;
    setAddingArticleVideo(true);
    try {
      const res = await uploadAnyFile(file);
      editorRef.current?.insertUploaded(res);
      setTimeout(refreshMedia, 120);
      toast.success(res.kind === "video" ? "Video caricato e aggiunto all'articolo" : "File caricato e aggiunto all'articolo");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setAddingArticleVideo(false); }
  };
  const [addingArticleFile, setAddingArticleFile] = useState(false);
  const uploadArticleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setAddingArticleFile(true);
    try {
      for (const f of list) editorRef.current?.insertUploaded(await uploadAnyFile(f));
      setTimeout(refreshMedia, 150);
      toast.success(`${list.length} file aggiunto/i all'articolo`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setAddingArticleFile(false); }
  };
  const removeArticleFile = (idx) => { editorRef.current?.removeNthAttachment(idx); setTimeout(refreshMedia, 120); };
  const removeArticleVideo = (idx) => { editorRef.current?.removeNthVideo(idx); setTimeout(refreshMedia, 120); };
  const [addImageLink, setAddImageLink] = useState("");
  const [addingArticleImage, setAddingArticleImage] = useState(false);
  const addArticleImageLink = () => {
    const url = (addImageLink || "").trim();
    if (!/^https?:\/\//i.test(url)) { toast.error("Inserisci un URL immagine valido (http/https)."); return; }
    editorRef.current?.insertImage(url);
    setAddImageLink("");
    setTimeout(refreshMedia, 120);
    toast.success("Immagine aggiunta in fondo all'articolo");
  };
  const uploadArticleImages = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setAddingArticleImage(true);
    try {
      for (const f of list) editorRef.current?.insertUploaded(await uploadAnyFile(f));
      setTimeout(refreshMedia, 150);
      toast.success(`${list.length} file aggiunto/i all'articolo`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setAddingArticleImage(false); }
  };
  const removeArticleImage = (idx) => { editorRef.current?.removeNthImage(idx); setTimeout(refreshMedia, 120); };
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewSending, setPreviewSending] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);
  useEffect(() => {
    try { setCustomPresets(JSON.parse(localStorage.getItem("opes_email_presets") || "[]")); } catch { /* ignore */ }
  }, []);
  const persistPresets = (next) => { setCustomPresets(next); try { localStorage.setItem("opes_email_presets", JSON.stringify(next)); } catch { /* ignore */ } };
  const saveCurrentTheme = () => {
    const next = [...customPresets, { key: `custom-${Date.now()}`, label: `Mio tema ${customPresets.length + 1}`, accent_color: emailTpl.accent_color || "#D4AF37", bg_color: emailTpl.bg_color || "#f4f4f5", custom: true }];
    persistPresets(next);
    toast.success("Tema salvato tra i temi rapidi");
  };
  const removeCustomPreset = (key) => persistPresets(customPresets.filter((p) => p.key !== key));

  const openEmailModel = async () => {
    try {
      const { data } = await api.get("/settings/public");
      const e = data.email || {};
      setEmailTpl({
        header_title: e.header_title || "",
        accent_color: e.accent_color || "#D4AF37",
        footer_text: e.footer_text || "",
        bg_color: e.bg_color || "#f4f4f5",
        logo_url: e.logo_url || "",
      });
    } catch { setEmailTpl(emailTplEmpty); }
    setPreviewEmail(user?.email || "");
    setEmailTplOpen(true);
  };
  const saveEmailModel = async () => {
    setEmailTplSaving(true);
    try {
      await api.put("/settings/email", emailTpl);
      toast.success("Modello email salvato");
      setEmailTplOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEmailTplSaving(false); }
  };
  const sendTplPreview = async () => {
    if (!previewEmail) { toast.error("Inserisci un'email"); return; }
    setPreviewSending(true);
    try {
      await api.post("/settings/email/preview", { ...emailTpl, email: previewEmail, site_url: window.location.origin });
      toast.success(`Anteprima inviata a ${previewEmail}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPreviewSending(false); }
  };
  const saveAndSend = async () => {
    if (!previewEmail) { toast.error("Inserisci un'email per l'anteprima"); return; }
    setEmailTplSaving(true);
    try {
      await api.put("/settings/email", emailTpl);
      await api.post("/settings/email/preview", { ...emailTpl, email: previewEmail, site_url: window.location.origin });
      toast.success(`Modello salvato · anteprima inviata a ${previewEmail}`);
      setEmailTplOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEmailTplSaving(false); }
  };
  const uploadEmailLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEmailTpl((t) => ({ ...t, logo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Logo caricato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploadingLogo(false); }
  };

  const openNewsletter = (item) => {
    setNlDialog(item);
    setNlSegment(item.discipline || "");
    setNlEmail(user?.email || "");
    setNlConfirm(false);
  };
  const sendTest = async () => {
    if (!nlEmail) { toast.error("Inserisci un'email"); return; }
    setNlBusy(true);
    try {
      await api.post(`/news/${nlDialog.id}/test-send`, { email: nlEmail, site_url: window.location.origin });
      toast.success(`Anteprima inviata a ${nlEmail}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setNlBusy(false); }
  };
  const sendAll = async () => {
    setNlBusy(true);
    try {
      const { data } = await api.post(`/news/${nlDialog.id}/notify`, { site_url: window.location.origin, discipline: nlSegment });
      toast.success(`Newsletter inviata a ${data.sent} iscritti${data.failed ? ` (${data.failed} falliti)` : ""}`);
      setNlDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setNlBusy(false); }
  };
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const n = await api.get("/news");
      setNews(n.data);
      if (isAdmin) {
        const s = await api.get("/newsletter");
        setSubs(s.data);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };
  const openEdit = (item) => {
    setForm({ title: item.title, body: item.body, image_url: item.image_url || "", images: (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []), image_position: item.image_position || "50% 50%", video_url: item.video_url || "", discipline: item.discipline || "", category: item.category || "notizia", published: item.published !== false, featured: !!item.featured, publish_at: item.publish_at || "" });
    setEditingId(item.id);
    setOpen(true);
  };

  const doFeature = async (item, featured, notify) => {
    try {
      const site = encodeURIComponent(window.location.origin);
      await api.put(`/news/${item.id}?site_url=${site}&notify=${notify ? "true" : "false"}`, {
        title: item.title, body: item.body, date: item.date || "", image_url: item.image_url || "",
        images: (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []),
        image_position: item.image_position || "50% 50%", video_url: item.video_url || "",
        discipline: item.discipline || "", category: item.category || "notizia",
        published: item.published !== false, featured, publish_at: item.publish_at || "",
      });
      toast.success(!featured ? "Rimossa dall'evidenza" : notify ? "In evidenza · newsletter inviata agli iscritti" : "Messa in evidenza");
      setConfirmFeature(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleFeatured = (item) => {
    if (item.featured) { doFeature(item, false, false); return; }
    setConfirmFeature(item);
  };

  const uploadImage = async (files) => {
    const list = files && files.length ? Array.from(files) : [];
    if (!list.length) return;
    setUploadingImage(true);
    try {
      const added = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
      setForm((f) => { const images = [...(f.images || []), ...added]; return { ...f, images, image_url: images[0] || "" }; });
      toast.success(added.length > 1 ? `${added.length} immagini caricate` : "Immagine caricata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Caricamento immagine non riuscito");
    } finally {
      setUploadingImage(false);
    }
  };
  const removeImage = (idx) => setForm((f) => { const images = (f.images || []).filter((_, i) => i !== idx); return { ...f, images, image_url: images[0] || "" }; });

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Video caricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingVideo(false);
    }
  };

  const save = async () => {
    const bodyHtml = editorRef.current?.getHTML?.() || form.body;
    if (!form.title || !bodyHtml) { toast.error("Titolo e testo obbligatori"); return; }
    setSaving(true);
    const site = encodeURIComponent(window.location.origin);
    const payload = { ...form, body: bodyHtml, image_url: (form.images && form.images[0]) || form.image_url || "" };
    try {
      if (editingId) { await api.put(`/news/${editingId}?site_url=${site}`, payload); toast.success("Notizia aggiornata"); }
      else { await api.post("/news", payload); toast.success(form.published ? "Notizia pubblicata" : "Bozza salvata"); }
      setOpen(false); reset(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/news/${id}`); toast.success("Notizia eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-muted-foreground">Notizie su eventi e gare organizzate.</p>
        {isAdmin && (
          <div className="flex items-center gap-2">
          <Button data-testid="email-model-btn" variant="outline" onClick={openEmailModel} className="border-white/15"><Settings2 className="w-4 h-4 mr-1" /> Modello email</Button>
          <Dialog open={emailTplOpen} onOpenChange={setEmailTplOpen}>
            <DialogContent data-testid="email-model-dialog" className="bg-[#0F172A] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Modello Email Newsletter</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground -mt-2">Personalizza l'aspetto delle email inviate agli iscritti. Le impostazioni valgono per tutte le newsletter delle notizie.</p>
              <div className="mt-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Temi rapidi</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {emailPresets.map((p) => {
                    const active = emailTpl.accent_color?.toLowerCase() === p.accent_color.toLowerCase() && emailTpl.bg_color?.toLowerCase() === p.bg_color.toLowerCase();
                    return (
                      <button
                        key={p.key}
                        type="button"
                        data-testid={`email-preset-${p.key}`}
                        onClick={() => setEmailTpl({ ...emailTpl, accent_color: p.accent_color, bg_color: p.bg_color })}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${active ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}
                      >
                        <span className="flex -space-x-1">
                          <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ background: p.accent_color }} />
                          <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ background: p.bg_color }} />
                        </span>
                        {p.label}
                      </button>
                    );
                  })}
                  {customPresets.map((p) => {
                    const active = emailTpl.accent_color?.toLowerCase() === p.accent_color.toLowerCase() && emailTpl.bg_color?.toLowerCase() === p.bg_color.toLowerCase();
                    return (
                      <span key={p.key} data-testid={`email-preset-${p.key}`} className={`inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full border text-sm transition-colors ${active ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                        <button type="button" onClick={() => setEmailTpl({ ...emailTpl, accent_color: p.accent_color, bg_color: p.bg_color })} className="flex items-center gap-2">
                          <span className="flex -space-x-1">
                            <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ background: p.accent_color }} />
                            <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ background: p.bg_color }} />
                          </span>
                          {p.label}
                        </button>
                        <button type="button" data-testid={`email-preset-remove-${p.key}`} onClick={() => removeCustomPreset(p.key)} title="Elimina tema" className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    );
                  })}
                  <button type="button" data-testid="email-preset-save" onClick={saveCurrentTheme} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-gold/40 text-sm text-gold hover:bg-gold/10 transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Salva tema attuale
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-2">
                <div className="space-y-4">
                  <div>
                    <Label>Logo (opzionale)</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input data-testid="email-logo-url" value={emailTpl.logo_url} onChange={(e) => setEmailTpl({ ...emailTpl, logo_url: e.target.value })} placeholder="URL logo o carica un file" className="flex-1" />
                      <label className="shrink-0">
                        <input type="file" className="hidden" onChange={(e) => uploadEmailLogo(e.target.files?.[0])} data-testid="email-logo-upload" />
                        <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-white/15 text-sm cursor-pointer hover:bg-white/5">{uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</span>
                      </label>
                    </div>
                    {emailTpl.logo_url && (
                      <div className="flex items-center gap-2 mt-2">
                        <img src={emailTpl.logo_url} alt="logo" className="h-10 bg-white rounded p-1" data-testid="email-logo-preview" />
                        <button type="button" onClick={() => setEmailTpl({ ...emailTpl, logo_url: "" })} className="text-xs text-red-400 hover:underline" data-testid="email-logo-clear">Rimuovi</button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Titolo intestazione</Label>
                    <Input data-testid="email-header-title" value={emailTpl.header_title} onChange={(e) => setEmailTpl({ ...emailTpl, header_title: e.target.value })} placeholder="OPES Ginnastiche" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Colore accento (pulsanti / titolo)</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <input type="color" data-testid="email-accent-color" value={emailTpl.accent_color || "#D4AF37"} onChange={(e) => setEmailTpl({ ...emailTpl, accent_color: e.target.value })} className="h-10 w-14 rounded border border-white/15 bg-transparent cursor-pointer" />
                      <Input value={emailTpl.accent_color} onChange={(e) => setEmailTpl({ ...emailTpl, accent_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Colore di sfondo</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <input type="color" data-testid="email-bg-color" value={emailTpl.bg_color || "#f4f4f5"} onChange={(e) => setEmailTpl({ ...emailTpl, bg_color: e.target.value })} className="h-10 w-14 rounded border border-white/15 bg-transparent cursor-pointer" />
                      <Input value={emailTpl.bg_color} onChange={(e) => setEmailTpl({ ...emailTpl, bg_color: e.target.value })} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Testo del footer</Label>
                    <textarea data-testid="email-footer-text" value={emailTpl.footer_text} onChange={(e) => setEmailTpl({ ...emailTpl, footer_text: e.target.value })} rows={3} placeholder="Ricevi questa email perché sei iscritto alla newsletter di OPES Ginnastiche." className="mt-1.5 w-full rounded-md bg-white/5 border border-white/15 px-3 py-2 text-sm resize-y" />
                  </div>
                </div>
                <div>
                  <Label>Anteprima</Label>
                  <div data-testid="email-preview" className="mt-1.5 rounded-lg p-4 overflow-hidden" style={{ background: emailTpl.bg_color || "#f4f4f5" }}>
                    <div className="bg-white rounded-lg p-5 max-w-full" style={{ color: "#111" }}>
                      {emailTpl.logo_url && <img src={emailTpl.logo_url} alt="logo" className="h-9 mb-3" />}
                      <div className="text-[11px] tracking-widest uppercase mb-1" style={{ color: emailTpl.accent_color || "#D4AF37" }}>{emailTpl.header_title || "OPES Ginnastiche"}</div>
                      <div className="text-lg font-bold text-[#111]">Titolo della notizia</div>
                      <div className="text-sm text-[#333] mt-2 leading-relaxed">Questo è un esempio del corpo dell'email che riceveranno gli iscritti alla newsletter.</div>
                      <span className="inline-block mt-4 px-4 py-2 rounded-full text-sm font-semibold" style={{ background: emailTpl.accent_color || "#D4AF37", color: "#020617" }}>Scopri di più</span>
                      <div className="mt-5 pt-3 border-t border-[#eee] text-[11px] text-[#999]">{emailTpl.footer_text || "Ricevi questa email perché sei iscritto alla newsletter di OPES Ginnastiche."}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-4">
                <Label className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-gold" /> Invia un'anteprima a te stesso</Label>
                <p className="text-xs text-muted-foreground mt-1">Ricevi un'email di prova con il modello attuale (anche se non ancora salvato).</p>
                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <Input data-testid="email-preview-to" type="email" value={previewEmail} onChange={(e) => setPreviewEmail(e.target.value)} placeholder="tua@email.it" className="flex-1" />
                  <Button data-testid="email-preview-send" variant="outline" onClick={sendTplPreview} disabled={previewSending} className="border-gold/40 text-gold hover:bg-gold/10 shrink-0">
                    {previewSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Invia anteprima
                  </Button>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setEmailTplOpen(false)} className="border-white/15">Annulla</Button>
                <Button data-testid="email-model-save-send" variant="outline" onClick={saveAndSend} disabled={emailTplSaving} className="border-gold/40 text-gold hover:bg-gold/10">
                  {emailTplSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Salva e invia anteprima
                </Button>
                <Button data-testid="email-model-save" onClick={saveEmailModel} disabled={emailTplSaving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                  {emailTplSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Salva modello
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-news-btn" onClick={reset} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Nuova Notizia
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Modifica Notizia" : "Nuova Notizia"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Titolo</Label><Input data-testid="news-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                <div>
                  <Label>Disciplina (per suggerire notizie correlate)</Label>
                  <Select value={form.discipline || "generale"} onValueChange={(v) => setForm({ ...form, discipline: v === "generale" ? "" : v })}>
                    <SelectTrigger data-testid="news-discipline-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="generale">Generale (nessuna disciplina)</SelectItem>
                      <SelectItem value="artistica">Ginnastica Artistica</SelectItem>
                      <SelectItem value="ritmica">Ginnastica Ritmica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Contenuto</Label>
                  <div className="mt-1.5"><RichTextEditor ref={editorRef} value={form.body} onChange={(v) => { setForm((f) => ({ ...f, body: v })); refreshMedia(); }} /></div>
                  <p className="text-[11px] text-muted-foreground mt-1">Formatta il testo e inserisci più immagini, video (YouTube/Vimeo o file mp4 caricato) e gallerie di foto scorrevoli con i pulsanti nella barra: vengono inseriti nel punto del cursore e sono spostabili nel testo.</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <Label className="flex items-center gap-1.5"><Image className="w-4 h-4 text-gold" /> Immagini dell'articolo ({articleImages.length})</Label>
                  <p className="text-[11px] text-muted-foreground mt-1 mb-3">Aggiungi più immagini: carica una o più foto dal dispositivo (qualsiasi formato) oppure incolla un link URL. Vengono aggiunte in fondo all'articolo, nell'ordine di inserimento, e puoi rimuoverle singolarmente.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="add-image-link-input" value={addImageLink} onChange={(e) => setAddImageLink(e.target.value)} placeholder="Link immagine (https://…)" className="flex-1"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArticleImageLink(); } }} />
                    <Button type="button" data-testid="add-image-link-btn" onClick={addArticleImageLink} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold shrink-0"><Plus className="w-4 h-4 mr-1" /> Aggiungi immagine</Button>
                    <label data-testid="add-image-upload" className="inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer shrink-0">
                      {addingArticleImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica foto
                      <input type="file" multiple className="hidden" onChange={(e) => { uploadArticleImages(e.target.files); e.target.value = ""; }} />
                    </label>
                  </div>
                  {articleImages.length > 0 && (
                    <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="article-images-list">
                      {articleImages.map((im, i) => (
                        <li key={`${im.src}-${i}`} data-testid={`article-image-${i}`} className="relative group rounded-md overflow-hidden border border-white/10">
                          <img src={mediaUrl(im.src)} alt="" className="w-full h-24 object-cover" />
                          <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-gold text-[#020617] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                          <button type="button" data-testid={`article-image-remove-${i}`} onClick={() => removeArticleImage(i)} title="Rimuovi immagine" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white hover:bg-destructive flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <Label className="flex items-center gap-1.5"><Video className="w-4 h-4 text-gold" /> Video dell'articolo ({articleVideos.length})</Label>
                  <p className="text-[11px] text-muted-foreground mt-1 mb-3">Aggiungi più video: incolla un link YouTube/Vimeo oppure carica un file. Vengono aggiunti in fondo all'articolo, nell'ordine di inserimento, e puoi rimuoverli singolarmente.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="add-video-link-input" value={addVideoLink} onChange={(e) => setAddVideoLink(e.target.value)} placeholder="Link YouTube o Vimeo" className="flex-1"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArticleVideoLink(); } }} />
                    <Button type="button" data-testid="add-video-link-btn" onClick={addArticleVideoLink} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold shrink-0"><Plus className="w-4 h-4 mr-1" /> Aggiungi video</Button>
                    <label data-testid="add-video-upload" className="inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer shrink-0">
                      {addingArticleVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica file
                      <input type="file" className="hidden" onChange={(e) => { uploadArticleVideoFile(e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                  {articleVideos.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="article-videos-list">
                      {articleVideos.map((v, i) => (
                        <li key={`${v.src}-${i}`} data-testid={`article-video-${i}`} className="flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2">
                          <span className="w-6 h-6 shrink-0 rounded-full bg-gold/15 text-gold text-xs font-bold flex items-center justify-center">{i + 1}</span>
                          <div className="w-32 h-20 shrink-0 rounded overflow-hidden bg-black border border-white/10" data-testid={`article-video-preview-${i}`}>
                            {v.kind === "file"
                              ? <video src={mediaUrl(v.src)} controls preload="metadata" className="w-full h-full object-cover" />
                              : youtubeThumb(v.src)
                                ? <img src={youtubeThumb(v.src)} alt="anteprima" className="w-full h-full object-cover" />
                                : <iframe src={v.src} title={`video-${i}`} className="w-full h-full" frameBorder="0" allowFullScreen />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs px-2 py-0.5 rounded-full border border-white/15 text-muted-foreground">{v.label}</span>
                            <p className="text-xs text-muted-foreground truncate mt-1">{v.src}</p>
                          </div>
                          <button type="button" data-testid={`article-video-remove-${i}`} onClick={() => removeArticleVideo(i)} title="Rimuovi video" className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <Label className="flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-gold" /> Allegati dell'articolo ({articleFiles.length})</Label>
                  <p className="text-[11px] text-muted-foreground mt-1 mb-3">Carica qualsiasi file (PDF, Word, Excel, PowerPoint, zip, audio…): viene mostrato come scheda con pulsanti Apri e Scarica in fondo all'articolo.</p>
                  <label data-testid="add-file-upload" className="inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
                    {addingArticleFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica file
                    <input type="file" multiple className="hidden" onChange={(e) => { uploadArticleFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                  {articleFiles.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="article-files-list">
                      {articleFiles.map((f, i) => (
                        <li key={`${f.src}-${i}`} data-testid={`article-file-${i}`} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0"><AttachmentCard url={f.src} name={f.name} size={f.size} className="my-0" testid={`article-file-card-${i}`} /></div>
                          <button type="button" data-testid={`article-file-remove-${i}`} onClick={() => removeArticleFile(i)} title="Rimuovi allegato" className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <Label>Immagini di copertina (facoltativo)</Label>
                  <p className="text-xs text-muted-foreground mt-1">Aggiungi una o più immagini: la prima è la copertina principale (riposizionabile), le altre sono mostrate in ordine nell'articolo. Trascina con la maniglia per riordinarle.</p>
                  {(form.images || []).length > 0 && (
                    <Reorder.Group axis="y" values={form.images || []} onReorder={(next) => setForm((f) => ({ ...f, images: next, image_url: next[0] || "" }))} className="space-y-3 mt-2" data-testid="news-images-list">
                      {(form.images || []).map((url, idx) => (
                        <NewsImageItem
                          key={url}
                          url={url}
                          idx={idx}
                          position={form.image_position}
                          onReposition={(p) => setForm((f) => ({ ...f, image_position: p }))}
                          onRemove={() => removeImage(idx)}
                        />
                      ))}
                    </Reorder.Group>
                  )}
                  <label data-testid="news-image-upload" className="mt-2 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi immagine
                    <input data-testid="news-image-input" type="file" multiple className="hidden" onChange={(e) => { uploadImage(e.target.files); e.target.value = ""; }} />
                  </label>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Video className="w-4 h-4 text-gold" /> Video (facoltativo)</Label>
                  <Input data-testid="news-video-input" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="mt-1.5" placeholder="Link YouTube, Vimeo o URL .mp4" />
                  <div className="flex items-center gap-2 mt-2">
                    <label data-testid="news-video-upload" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-gold/50 cursor-pointer">
                      {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica video
                      <input type="file" className="hidden" onChange={(e) => uploadVideo(e.target.files?.[0])} />
                    </label>
                    {form.video_url && <button type="button" data-testid="news-video-clear" onClick={() => setForm({ ...form, video_url: "" })} className="text-sm text-muted-foreground hover:text-destructive">Rimuovi</button>}
                  </div>
                  {form.video_url && !getVideoEmbed(form.video_url) && <p className="text-xs text-destructive mt-1">Link video non riconosciuto (usa YouTube, Vimeo o un file .mp4).</p>}
                  {getVideoEmbed(form.video_url) && <div className="mt-2 max-w-sm"><BlockView block={{ type: "video", url: form.video_url }} /></div>}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/10">
                  <label data-testid="news-featured-toggle" className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${form.featured ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                    <Checkbox checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} />
                    <Star className="w-4 h-4" /> <span className="text-sm">Metti in evidenza</span>
                  </label>
                  <label data-testid="news-draft-toggle" className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${!form.published ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                    <Checkbox checked={!form.published} onCheckedChange={(v) => setForm({ ...form, published: !v })} />
                    <span className="text-sm">Salva come bozza (non pubblica)</span>
                  </label>
                </div>
                {!form.published && (
                  <div data-testid="news-schedule">
                    <Label>Programma pubblicazione automatica (facoltativo)</Label>
                    <Input data-testid="news-publish-at" type="datetime-local" value={form.publish_at ? form.publish_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, publish_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="mt-1.5 max-w-xs" />
                    <p className="text-xs text-muted-foreground mt-1">La bozza verrà pubblicata automaticamente alla data e ora scelte.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="save-news-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Aggiorna" : (form.published ? "Pubblica" : "Salva bozza")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {isAdmin && news.some((n) => n.published === false && n.publish_at && new Date(n.publish_at) > new Date()) && (
        <div data-testid="scheduled-panel" className="mb-6 rounded-xl border border-amber-400/25 bg-gradient-to-b from-amber-400/5 to-transparent p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-300 uppercase tracking-wider mb-3"><CalendarDays className="w-4 h-4" /> Notizie programmate</h3>
          <ul className="space-y-2">
            {[...news]
              .filter((n) => n.published === false && n.publish_at && new Date(n.publish_at) > new Date())
              .sort((a, b) => new Date(a.publish_at) - new Date(b.publish_at))
              .map((n) => (
                <li key={n.id} data-testid={`scheduled-${n.id}`} className="flex items-center justify-between gap-3 text-sm">
                  <button onClick={() => openEdit(n)} className="flex-1 min-w-0 truncate text-left hover:text-gold transition-colors">{n.title}</button>
                  <span className="flex items-center gap-1.5 text-amber-300 shrink-0"><CalendarDays className="w-3.5 h-3.5" />{new Date(n.publish_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </li>
              ))}
          </ul>
        </div>
      )}

        <div data-testid="most-read-panel" className="mb-6 rounded-xl border border-gold/20 bg-gradient-to-b from-gold/5 to-transparent p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gold uppercase tracking-wider mb-3"><Eye className="w-4 h-4" /> Notizie più lette</h3>
          <ol className="space-y-2">
            {[...news].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5).map((n, idx) => (
              <li key={n.id} data-testid={`most-read-${idx}`} className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 shrink-0 rounded-full bg-gold/15 text-gold font-display flex items-center justify-center text-xs">{idx + 1}</span>
                <Link to={`/notizia/${n.id}`} className="flex-1 min-w-0 truncate hover:text-gold transition-colors">{n.title}</Link>
                <span className="flex items-center gap-1.5 text-muted-foreground shrink-0"><Eye className="w-3.5 h-3.5 text-gold" />{n.views || 0}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {news.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-white/10 rounded-xl">Nessuna notizia pubblicata.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {news.map((item, i) => (
            <motion.div key={item.id} data-testid={`news-card-${item.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-xl border border-white/10 bg-[#0F172A] overflow-hidden hover:border-gold/40 transition-colors flex flex-col">
              {item.image_url && <img src={mediaUrl(item.image_url)} alt={item.title} className="w-full h-44 object-cover" style={{ objectPosition: item.image_position || "50% 50%" }} />}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-gold/40 text-gold flex items-center gap-1"><Newspaper className="w-3 h-3" />Notizia</Badge>
                    {item.featured && <Badge className="bg-gold text-[#020617] flex items-center gap-1"><Star className="w-3 h-3" fill="currentColor" />In evidenza</Badge>}
                    {item.published === false && <Badge variant="outline" data-testid={`news-draft-${item.id}`} className="border-amber-400/50 text-amber-300">{item.publish_at ? `Programmata · ${new Date(item.publish_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : "Bozza"}</Badge>}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-3">
                      <button data-testid={`newsletter-news-${item.id}`} onClick={() => openNewsletter(item)} title="Invia / anteprima newsletter" className="text-muted-foreground hover:text-gold transition-colors"><Mail className="w-4 h-4" /></button>
                      <button data-testid={`pin-news-${item.id}`} onClick={() => toggleFeatured(item)} title={item.featured ? "Rimuovi dall'evidenza" : "Metti in evidenza"} className={`transition-colors ${item.featured ? "text-gold" : "text-muted-foreground hover:text-gold"}`}><Star className="w-4 h-4" fill={item.featured ? "currentColor" : "none"} /></button>
                      <button data-testid={`edit-news-${item.id}`} onClick={() => openEdit(item)} className="text-muted-foreground hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`delete-news-${item.id}`} onClick={() => remove(item.id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-semibold mt-3"><Link to={`/notizia/${item.id}`} data-testid={`news-link-${item.id}`} className="hover:text-gold transition-colors">{item.title}</Link></h3>
                {item.video_url && getVideoEmbed(item.video_url) && (
                  <div className="mt-3" data-testid={`news-video-${item.id}`}>
                    <VideoThumb url={item.video_url} poster={item.image_url ? mediaUrl(item.image_url) : null} />
                  </div>
                )}
                <ArticleBody text={item.body} className="text-sm text-muted-foreground mt-2 flex-1" showNewsletterCta />
                {isAdmin && (item.featured_notified_at || item.notified_at) && (
                  <p data-testid={`news-sendlog-${item.id}`} className="flex items-center gap-1.5 text-xs text-[#10B981] mt-3"><Mail className="w-3.5 h-3.5" /> Newsletter inviata a {item.featured_notified_count ?? item.notified_count ?? 0} iscritti il {new Date(item.featured_notified_at || item.notified_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}{typeof item.email_opens === "number" ? ` · ${item.email_opens} aperture` : ""}</p>
                )}
                <div className="flex items-center justify-between gap-3 mt-4">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays className="w-3.5 h-3.5 text-gold" />{new Date(item.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
                    {isAdmin && <span data-testid={`news-views-${item.id}`} className="flex items-center gap-1.5 text-xs text-muted-foreground"><Eye className="w-3.5 h-3.5 text-gold" />{item.views || 0}</span>}
                  </div>
                  <ShareButtons url={`${window.location.origin}/notizia/${item.id}`} title={item.title} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-gold" />
            <h3 className="text-lg font-semibold">Iscritti alla Newsletter <span className="text-gold" data-testid="subs-count">({subs.length})</span></h3>
          </div>
          {subs.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nessun iscritto ancora.</p>
          ) : (
            <div className="rounded-xl border border-white/10 bg-[#0F172A] divide-y divide-white/5">
              {subs.map((s) => (
                <div key={s.id} data-testid={`sub-${s.id}`} className="flex items-center justify-between px-5 py-3">
                  <span className="font-medium">{s.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">{s.email}</span>
                    <button data-testid={`delete-sub-${s.id}`} onClick={async () => { await api.delete(`/newsletter/${s.id}`); load(); }} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!confirmFeature} onOpenChange={(o) => !o && setConfirmFeature(null)}>
        <DialogContent data-testid="confirm-feature-dialog" className="bg-[#0F172A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-gold" fill="currentColor" /> Metti in evidenza</DialogTitle>
          </DialogHeader>
          {confirmFeature && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Vuoi mettere in evidenza <span className="text-foreground font-medium">"{confirmFeature.title}"</span>?</p>
              {confirmFeature.featured_notified ? (
                <p className="text-xs text-amber-300">La newsletter per questa notizia è già stata inviata in precedenza: non verrà reinviata.</p>
              ) : (
                <p className="text-sm text-muted-foreground">Vuoi anche <span className="text-foreground font-medium">inviare subito la newsletter</span> a tutti gli iscritti{confirmFeature.discipline ? ` (${confirmFeature.discipline})` : ""}?</p>
              )}
              <div className="flex flex-col gap-2 pt-2">
                {!confirmFeature.featured_notified && (
                  <Button data-testid="confirm-feature-send" onClick={() => doFeature(confirmFeature, true, true)} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold w-full">Sì, metti in evidenza e invia newsletter</Button>
                )}
                <Button data-testid="confirm-feature-only" variant="outline" onClick={() => doFeature(confirmFeature, true, false)} className="border-white/15 w-full">Solo in evidenza (nessun invio)</Button>
                <Button data-testid="confirm-feature-cancel" variant="ghost" onClick={() => setConfirmFeature(null)} className="w-full">Annulla</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!nlDialog} onOpenChange={(o) => !o && setNlDialog(null)}>
        <DialogContent data-testid="newsletter-dialog" className="bg-[#0F172A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-gold" /> Newsletter</DialogTitle>
          </DialogHeader>
          {nlDialog && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground truncate">"{nlDialog.title}"</p>
              {(nlDialog.notified_at || nlDialog.featured_notified_at) && (
                <p className="text-xs text-[#10B981]">Già inviata a {nlDialog.notified_count ?? nlDialog.featured_notified_count ?? 0} iscritti il {new Date(nlDialog.notified_at || nlDialog.featured_notified_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
              )}
              <div>
                <Label>Destinatari (segmento)</Label>
                <Select value={nlSegment || "tutti"} onValueChange={(v) => setNlSegment(v === "tutti" ? "" : v)}>
                  <SelectTrigger data-testid="nl-segment-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti gli iscritti</SelectItem>
                    <SelectItem value="artistica">Solo Ginnastica Artistica</SelectItem>
                    <SelectItem value="ritmica">Solo Ginnastica Ritmica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invia un'anteprima a te</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="nl-test-email" value={nlEmail} onChange={(e) => setNlEmail(e.target.value)} placeholder="tua@email.it" className="h-9" />
                  <Button data-testid="nl-test-send" variant="outline" onClick={sendTest} disabled={nlBusy} className="border-white/15 h-9 shrink-0">Invia prova</Button>
                </div>
              </div>
              <div className="pt-2 border-t border-white/10">
                {!nlConfirm ? (
                  <Button data-testid="nl-send-all" onClick={() => setNlConfirm(true)} className="w-full bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                    {(nlDialog.notified_at || nlDialog.featured_notified_at) ? "Reinvia a tutti gli iscritti" : "Invia a tutti gli iscritti"}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-300 text-center">Confermi l'invio della newsletter{nlSegment ? ` (${nlSegment})` : ""}?</p>
                    <div className="flex gap-2">
                      <Button data-testid="nl-send-confirm" onClick={sendAll} disabled={nlBusy} className="flex-1 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">{nlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma invio"}</Button>
                      <Button data-testid="nl-send-cancel" variant="ghost" onClick={() => setNlConfirm(false)} className="flex-1">Annulla</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
